import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel,
} from '../ui/alert-dialog';
import api, { leadsAPI } from '../../lib/api';
import {
  Building2, Users, Target, Mail, Phone, CheckCircle2,
  Circle, Sparkles, ArrowRight, Loader2, ChevronRight,
} from 'lucide-react';

/* ─────────────────────────────────────────────────────────────────────
 * Iter 60 #1D — Hybrid Welcome Dashboard
 *
 * A "First hour at Lockene" Linear-style numbered checklist with
 * personalized hero context (company, industry, ICP region, lead count).
 * Reads from /api/onboarding/status + /api/leads/_bootstrap and is
 * dismissible. Auto-hides once all milestones are checked OR after 7 days.
 * ───────────────────────────────────────────────────────────────────── */

const DISMISS_KEY = 'first_hour_checklist_dismissed';
const COMPLETED_KEY = 'first_hour_checklist_completed_at';

/** Extract a clean domain (e.g. "acme.com") from a raw website URL.
 *  Used as a graceful fallback for the welcome hero before Opus auto-inference
 *  resolves the actual company_name into the tenants doc. */
function extractDomain(url) {
  if (!url || typeof url !== 'string') return null;
  try {
    const cleaned = url.trim().replace(/^https?:\/\//i, '').replace(/^www\./i, '');
    const host = cleaned.split('/')[0].split('?')[0];
    return host && host.includes('.') ? host : null;
  } catch {
    return null;
  }
}

function StepRow({ idx, done, busy, icon: Icon, label, sub, ctaLabel, onCta, accent }) {
  return (
    <button
      onClick={onCta}
      disabled={!onCta}
      className={`group w-full flex items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-all
        ${done ? 'opacity-60 hover:opacity-90' : 'hover:bg-muted/50'}
        ${onCta ? 'cursor-pointer' : 'cursor-default'}`}
      data-testid={`first-hour-step-${idx}`}
    >
      <div className="flex items-center gap-3 shrink-0">
        <span className={`text-[10px] font-mono font-bold w-4 ${done ? 'text-emerald-500' : 'text-muted-foreground/60'}`}>
          {String(idx).padStart(2, '0')}
        </span>
        <span className={`relative w-7 h-7 rounded-lg flex items-center justify-center shrink-0
          ${done ? 'bg-emerald-500/12' : busy ? 'bg-blue-500/12' : `bg-${accent || 'muted'}/40`}`}>
          {done ? (
            <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
          ) : busy ? (
            <Loader2 className="h-3.5 w-3.5 text-blue-500 animate-spin" />
          ) : (
            <Icon className={`h-3.5 w-3.5 ${accent ? `text-${accent.split('-')[0]}-500` : 'text-muted-foreground'}`} />
          )}
        </span>
      </div>
      <div className="flex-1 min-w-0">
        <div className={`text-sm font-medium leading-tight ${done ? 'line-through text-muted-foreground' : 'text-foreground'}`}>
          {label}
        </div>
        {sub && (
          <div className="text-[11px] text-muted-foreground/80 truncate mt-0.5">{sub}</div>
        )}
      </div>
      {!done && ctaLabel && (
        <span className="hidden sm:flex items-center gap-1 text-[11px] font-medium text-muted-foreground group-hover:text-foreground shrink-0">
          {ctaLabel}
          <ChevronRight className="h-3 w-3 transition-transform group-hover:translate-x-0.5" />
        </span>
      )}
    </button>
  );
}

export default function FirstHourChecklistCard() {
  const navigate = useNavigate();
  const [hidden, setHidden] = useState(false);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [leadStats, setLeadStats] = useState({ total: 0, with_email: 0, with_phone: 0, top_city: null });
  const [icpRegion, setIcpRegion] = useState(null);
  // Iter 62.3 Phase B — credit-aware single-reveal flow from the dashboard
  const [revealCandidate, setRevealCandidate] = useState(null);
  const [credits, setCredits] = useState(null);
  const [revealConfirmOpen, setRevealConfirmOpen] = useState(false);
  const [revealing, setRevealing] = useState(false);

  useEffect(() => {
    // Honor dismissal + 7-day expiry
    try {
      if (localStorage.getItem(DISMISS_KEY) === 'true') {
        setHidden(true);
        return;
      }
      const completedAt = localStorage.getItem(COMPLETED_KEY);
      if (completedAt && Date.now() - parseInt(completedAt, 10) > 7 * 24 * 60 * 60 * 1000) {
        setHidden(true);
        return;
      }
    } catch { /* ignore */ }

    let cancelled = false;
    (async () => {
      try {
        const [statusRes, leadsRes, icpRes, cadencesRes, creditsRes] = await Promise.allSettled([
          api.get('/api/onboarding/status'),
          api.get('/api/leads/?limit=200'),
          api.get('/api/icp/').catch(() => ({ data: null })),
          api.get('/api/brain/cadences').catch(() => ({ data: { cadences: [] } })),
          leadsAPI.getRevealCredits().catch(() => ({ data: null })),
        ]);
        if (cancelled) return;
        const sd = (statusRes.status === 'fulfilled' && statusRes.value.data) || {};
        // Iter 60.3 — defensive cadence detection: brain may auto-create
        // cadence AFTER onboarding finishes, leaving `cadence_configured`
        // false even though a real cadence exists. Cross-check with the
        // actual cadences list so step 04 doesn't stay unchecked.
        const cadences = (cadencesRes.status === 'fulfilled' && cadencesRes.value.data?.cadences) || [];
        const hasActiveCadence = Array.isArray(cadences) && cadences.some(
          (c) => !c.lead_id && (c.is_active !== false)
        );
        if (hasActiveCadence && !sd.cadence_configured) {
          sd.cadence_configured = true;  // optimistic local override
        }
        // Iter 61 — same defensive override for ICP. The `icp_ready` flag
        // can stay false if onboarding's auto-infer step skipped (Apollo
        // depleted, scrape error, etc.) but the ICP doc itself was created
        // later by the lead-pipeline auto-bootstrap. Cross-check the actual
        // ICP endpoint so step 02 doesn't stay stuck on "AI is building".
        const icpData = (icpRes.status === 'fulfilled' && icpRes.value.data) || null;
        const icpInner = icpData?.icp || icpData || null;
        const icpExists = !!(icpInner && (icpInner.industries?.length || icpInner.titles?.length || icpInner.ideal_lead_titles?.length || icpInner.industry || icpInner.target_personas?.length));
        if (icpExists && !sd.icp_ready) {
          sd.icp_ready = true;
        }
        setStatus(sd);

        const leads = (leadsRes.status === 'fulfilled' && (leadsRes.value.data?.data || leadsRes.value.data)) || [];
        if (Array.isArray(leads)) {
          const withEmail = leads.filter((l) => l.email).length;
          const withPhone = leads.filter((l) => l.phone).length;
          // Pick top city if available
          const cityCounts = {};
          leads.forEach((l) => {
            const c = l.city || (l.location || '').split(',')[0]?.trim();
            if (c) cityCounts[c] = (cityCounts[c] || 0) + 1;
          });
          const topCity = Object.entries(cityCounts).sort(([, a], [, b]) => b - a)[0]?.[0] || null;
          setLeadStats({ total: leads.length, with_email: withEmail, with_phone: withPhone, top_city: topCity });

          // Iter 62.3 Phase B — pick the best phone-reveal candidate.
          // Filter rules: no phone yet, has Apollo lineage, NOT paused for review,
          //               has a verified email (signals real lead). Rank by ai_score desc.
          const candidates = leads
            .filter((l) =>
              !l.phone
              && (l.apollo_id || l.provider_id || (l.source || '').toLowerCase().includes('apollo'))
              && l.needs_review !== true
              && l.email
            )
            .sort((a, b) => (b.ai_score || 0) - (a.ai_score || 0));
          if (candidates.length > 0) {
            setRevealCandidate(candidates[0]);
          }
        }

        // Store credit balance for the reveal confirm dialog
        const creditsData = creditsRes.status === 'fulfilled' ? creditsRes.value?.data : null;
        if (creditsData) setCredits(creditsData);

        const icp = icpRes.status === 'fulfilled' ? icpRes.value?.data?.icp : null;
        if (icp) {
          const region = (icp.firmographic?.geographies || icp.geographies || [])[0] || null;
          setIcpRegion(region);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const steps = useMemo(() => {
    if (!status) return [];
    const profileDone = !!(status.company_name && (status.industry || status.company_size));
    const icpDone = !!status.icp_ready || !!status.brain_data_seeded;
    const leadsDone = leadStats.total > 0 || !!status.has_created_lead;
    const cadenceDone = !!status.cadence_configured;
    const revealDone = leadStats.with_phone >= 1;

    return [
      {
        key: 'profile', done: profileDone, busy: false,
        icon: Building2, accent: 'sky-500',
        label: profileDone
          ? `${status.company_name || 'Company'} · ${status.industry || 'Industry set'}`
          : 'Confirm your company profile',
        sub: profileDone ? 'Tenant profile loaded into the AI Brain' : 'Add company name + industry so the brain can plan outreach',
        ctaLabel: 'Open',
        // Iter 60.3 — fixed: /settings/profile didn't exist → use /profile
        onCta: () => navigate('/profile'),
      },
      {
        key: 'icp', done: icpDone, busy: !icpDone && !!status.company_name,
        icon: Target, accent: 'violet-500',
        label: icpDone
          ? `Your ICP is ready${icpRegion ? ` · ${icpRegion}` : ''}`
          : 'AI is building your ICP',
        sub: icpDone
          ? 'Firmographic + persona model generated by Lockene AI'
          : 'Reading your website + classifying buyers (under a minute)',
        ctaLabel: 'View',
        // Iter 60.3 — fixed: /icp didn't exist → use /brain/icp
        onCta: icpDone ? () => navigate('/brain/icp') : null,
      },
      {
        key: 'leads', done: leadsDone, busy: icpDone && !leadsDone,
        icon: Users, accent: 'emerald-500',
        label: leadsDone
          ? `${leadStats.total} ${leadStats.total === 1 ? 'lead' : 'leads'} matched in your pipeline`
          : 'Finding your top-matched leads',
        sub: leadsDone
          ? `${leadStats.with_email} verified email · ${leadStats.with_phone} phone${leadStats.top_city ? ` · top city: ${leadStats.top_city}` : ''}`
          : 'Pulling 3 ICP-matched prospects with verified emails',
        ctaLabel: 'Open Kanban',
        onCta: () => navigate('/leads'),
      },
      {
        key: 'cadence', done: cadenceDone, busy: false,
        icon: Mail, accent: 'amber-500',
        label: cadenceDone ? 'Outreach cadence is live' : 'Design a 6-step outreach cadence',
        sub: cadenceDone
          ? 'Brain enrolled your leads — track sends in Email Outbox'
          : 'Personalized email sequence tailored to your ICP',
        ctaLabel: cadenceDone ? 'Outbox' : 'Set up',
        // Iter 60.3 — fixed routes: '/cadences' doesn't exist (was 404).
        // Manage/view cadences live at /ai-agents/cadence-analytics; sends
        // are tracked in /brain/email-outbox (not /email-outbox).
        onCta: () => navigate(cadenceDone ? '/brain/email-outbox' : '/ai-agents/cadence-analytics'),
      },
      {
        key: 'reveal', done: revealDone, busy: false,
        icon: Phone, accent: 'rose-500',
        label: revealDone
          ? 'Phone numbers revealed for live calls'
          : revealCandidate
            ? `Reveal ${revealCandidate.name?.split(' ')[0] || 'top lead'}'s phone`
            : 'Reveal a phone to call your top lead',
        sub: revealDone
          ? 'Click a lead in Kanban to start an AI-assisted call'
          : credits && typeof credits.balance === 'number'
            ? `~$${(credits.cost_per_reveal || 0.0312).toFixed(3)} per reveal · balance $${credits.balance.toFixed(2)}`
            : 'Phone reveal · ~8 credits per number',
        ctaLabel: revealCandidate && !revealDone ? 'Reveal' : 'Pipeline',
        onCta: revealDone
          ? () => navigate('/leads')
          : revealCandidate
            ? () => setRevealConfirmOpen(true)
            : () => navigate('/leads'),
      },
    ];
  }, [status, leadStats, icpRegion, navigate, revealCandidate, credits]);

  const completed = steps.filter((s) => s.done).length;
  const total = steps.length;
  const pct = total ? Math.round((completed / total) * 100) : 0;

  // Auto-mark completed timestamp once everything is done
  useEffect(() => {
    if (total > 0 && completed === total) {
      try {
        if (!localStorage.getItem(COMPLETED_KEY)) {
          localStorage.setItem(COMPLETED_KEY, String(Date.now()));
        }
      } catch { /* ignore */ }
    }
  }, [completed, total]);

  const handleDismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, 'true'); } catch { /* ignore */ }
    setHidden(true);
  };

  // Iter 62.3 Phase B — credit-aware phone reveal triggered from the checklist
  const handleConfirmReveal = async () => {
    if (!revealCandidate || revealing) return;
    setRevealing(true);
    try {
      const res = await leadsAPI.revealContact(revealCandidate.id);
      const data = res.data || {};
      if (data.success && data.revealed) {
        toast.success(`Phone revealed for ${revealCandidate.name || 'lead'}`, {
          description: data.phone || data.email || 'Contact details unlocked',
        });
        // Refresh credits + local state so the step flips to done
        try {
          const next = await leadsAPI.getRevealCredits();
          if (next?.data) setCredits(next.data);
        } catch { /* ignore */ }
        setLeadStats((p) => ({ ...p, with_phone: (p.with_phone || 0) + 1 }));
        setRevealConfirmOpen(false);
        setRevealCandidate(null);
        // Take them to the lead so they can immediately start a call
        navigate(`/leads/${revealCandidate.id}`);
      } else if (data.success && !data.revealed) {
        toast.warning('No phone found — no credits charged', {
          description: data.message || 'Provider had no number for this contact.',
        });
        setRevealConfirmOpen(false);
      } else {
        toast.error(data.message || 'Failed to reveal contact');
      }
    } catch (error) {
      const detail = error.response?.data?.detail || 'Failed to reveal contact';
      if (error.response?.status === 402) {
        toast.error('Insufficient credits', { description: 'Top up to reveal more phones.' });
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Reveal failed');
      }
    } finally {
      setRevealing(false);
    }
  };

  if (hidden || loading || !status) return null;

  // Hide entirely if the user is past the welcome flow (everything done + ≥3 leads + cadence)
  if (completed === total && leadStats.total >= 3) return null;

  const heroCompany = status.company_name || extractDomain(status.website_url) || 'your company';
  const heroIndustry = status.industry;
  const heroLeads = leadStats.total;

  return (
    <Card
      className="mt-6 border-border/60 bg-gradient-to-br from-background to-muted/20 overflow-hidden animate-[fadeUp_0.4s_ease-out]"
      data-testid="first-hour-checklist-card"
    >
      <CardContent className="p-5 sm:p-6">
        {/* Hero strip */}
        <div className="flex items-start justify-between gap-3 mb-5">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500/15 to-violet-500/10 border border-blue-500/20 flex items-center justify-center shrink-0">
              <Sparkles className="h-4 w-4 text-blue-500" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="text-sm font-bold text-foreground tracking-tight">
                  Your first hour at Lockene
                </h3>
                <Badge
                  variant="outline"
                  className="h-5 px-1.5 text-[10px] font-mono font-semibold bg-emerald-500/8 text-emerald-600 dark:text-emerald-400 border-emerald-500/30"
                  data-testid="first-hour-progress-badge"
                >
                  {completed}/{total} · {pct}%
                </Badge>
              </div>
              <p className="text-[11px] text-muted-foreground mt-0.5 truncate">
                {heroIndustry ? (
                  <>
                    Brain is mapping <span className="font-medium text-foreground/80">{heroCompany}</span>
                    {' · '}<span className="font-medium text-foreground/80">{heroIndustry}</span>
                    {heroLeads > 0 && <> · {heroLeads} live lead{heroLeads !== 1 ? 's' : ''}</>}
                  </>
                ) : (
                  <>Welcome to <span className="font-medium text-foreground/80">{heroCompany}</span> · setting things up</>
                )}
              </p>
            </div>
          </div>
          <button
            onClick={handleDismiss}
            className="text-[10px] uppercase tracking-wider text-muted-foreground hover:text-foreground shrink-0 px-2 py-1 rounded hover:bg-muted/60 transition-colors"
            data-testid="first-hour-dismiss"
          >
            Dismiss
          </button>
        </div>

        {/* Slim progress rail */}
        <div className="h-1 rounded-full bg-muted/40 overflow-hidden mb-4" data-testid="first-hour-progress-rail">
          <div
            className="h-full rounded-full transition-all duration-700 ease-out"
            style={{
              width: `${Math.max(pct, 5)}%`,
              background: 'linear-gradient(90deg, #3b82f6, #8b5cf6, #10b981)',
            }}
          />
        </div>

        {/* Step list */}
        <div className="space-y-0.5">
          {steps.map((s, i) => (
            <StepRow
              key={s.key}
              idx={i + 1}
              done={s.done}
              busy={s.busy}
              icon={s.icon}
              accent={s.accent}
              label={s.label}
              sub={s.sub}
              ctaLabel={s.ctaLabel}
              onCta={s.onCta}
            />
          ))}
        </div>

        {/* Quick action footer */}
        {completed < total && (
          <div className="mt-4 pt-4 border-t border-border/40 flex items-center justify-between gap-3">
            <span className="text-[11px] text-muted-foreground">
              {completed === 0
                ? 'Tap any step to jump in.'
                : `${total - completed} step${total - completed !== 1 ? 's' : ''} left to unlock the full Brain.`}
            </span>
            <button
              onClick={() => navigate('/leads')}
              className="inline-flex items-center gap-1 text-[11px] font-semibold text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
              data-testid="first-hour-go-pipeline"
            >
              Open pipeline <ArrowRight className="h-3 w-3" />
            </button>
          </div>
        )}
      </CardContent>

      {/* Iter 62.3 Phase B — credit-aware phone-reveal confirm */}
      <AlertDialog open={revealConfirmOpen} onOpenChange={setRevealConfirmOpen}>
        <AlertDialogContent data-testid="reveal-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Reveal phone for {revealCandidate?.name || 'this lead'}?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm">
                <div>
                  Estimated cost:{' '}
                  <span className="font-semibold text-foreground" data-testid="reveal-cost">
                    ~${(credits?.cost_per_reveal || 0.0312).toFixed(3)}
                  </span>{' '}
                  <span className="text-muted-foreground">(~8 Apollo credits)</span>
                </div>
                {credits && typeof credits.balance === 'number' && (
                  <div data-testid="reveal-balance">
                    Your balance:{' '}
                    <span className={`font-semibold ${credits.balance < (credits.cost_per_reveal || 0.0312) ? 'text-rose-600' : 'text-emerald-600'}`}>
                      ${credits.balance.toFixed(2)}
                    </span>
                    {typeof credits.reveals_available === 'number' && (
                      <span className="text-muted-foreground">
                        {' · '}{credits.reveals_available} reveal{credits.reveals_available === 1 ? '' : 's'} available
                      </span>
                    )}
                  </div>
                )}
                {credits && typeof credits.balance === 'number' && credits.balance < (credits.cost_per_reveal || 0.0312) && (
                  <div className="text-rose-600 text-xs font-medium">
                    Insufficient credits — top up before revealing.
                  </div>
                )}
                {revealCandidate?.company_name && (
                  <div className="text-xs text-muted-foreground">
                    @ {revealCandidate.company_name}{revealCandidate.title ? ` · ${revealCandidate.title}` : ''}
                  </div>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              disabled={revealing}
              data-testid="reveal-cancel-btn"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); handleConfirmReveal(); }}
              disabled={
                revealing
                || !revealCandidate
                || (credits && typeof credits.balance === 'number' && credits.balance < (credits.cost_per_reveal || 0.0312))
              }
              data-testid="reveal-confirm-btn"
            >
              {revealing ? (
                <span className="inline-flex items-center gap-1.5">
                  <Loader2 className="h-3 w-3 animate-spin" /> Revealing…
                </span>
              ) : (
                'Reveal phone'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
